define('edap',['basil',
        'consolehelpers',
        'cookieparser',
        'doneable',
        'edaphelpers',
        'featureflag',
        'features',
        'playback',
        'edapUnderscore'], function(Basil, ConsoleHelpers, cookieParser, Doneable, edapHelpers, FeatureFlag, Features, Playback, _) {
    'use strict';

    /**
     *  Event-driven Data Abstraction Platform (EDAP)
     *
     *  EDAP is a front-end event bus that is purpose built to handle event-based analytics. Some of the primary
     *  advantages are:
     *   - Increases data integrity since all callbacks are based on the same event, with the same data
     *   - Allows for extending your data layer on a per-event basis without worrying about race conditions
     *   - Has a built-in history function to help understand what is happening on the event bus
     *   - Can handle page-transition events on the next page, therefore increasing the chances of data being collected
     *   - Event listeners have the ability to report when async operations are complete
     *
     *  @constructor
     *  @param {Object} [edapOptions] - Supported properties:
     *                                      {String} [dataLayerName] - name of the data layer in the window (default: "analyticsdatalayer")
     *                                      {String} [ns] - window namespace to expose EDAP (default: "edap")
     *                                      {Boolean} [noAMD] - when set to true it will not expose EDAP as a requirejs module
     */
    function Edap() {
        var persistentLogger = new Basil({
                namespace: 'edap',
                storages: ['local', 'cookie'],
                expireDays: 14
            }),
            loggingVersion = '1.0.0',
            loggingKey = 'delayProcessing',
            defaultCmdsFormat = {ver: loggingVersion, cmds: []},
            uuidRegex = new RegExp('^[a-z0-9]{8}-[a-z0-9]{4}-4[a-z0-9]{3}-[a-z0-9]{4}-[a-z0-9]{12}$'),
            edapListenerAddedName = 'edap.listener.added',
            edapListenerRemovedName = 'edap.listener.removed',
            edapError = 'edap.error',
            edapLoadCookie = 'edap.load.cookie',
            edapFlush = 'edap.flush',
            edapReset = 'edap.reset',
            // The list of all events that are triggered or listened to in core EDAP
            coreSupportedEvents = [
                edapListenerAddedName,
                edapListenerRemovedName,
                edapError,
                edapFlush,
                edapReset
            ],
            // The list of all events that should not be logged to console
            ignoredConsoleEvents = [
                edapListenerAddedName,
                edapListenerRemovedName
            ],
            configCookieData,
            env,
            edapThis,
            edapPublic,
            edapInternal;

        // Setup pointers to different objects for easier maintainability
        edapThis = this;
        edapThis.internal = {};
        edapThis['public'] = {};
        edapInternal = edapThis.internal;
        edapPublic = edapThis['public'];


        // Set the version
        edapThis.edapVersion = '1.1.0';


        /**
         *
         * Internal Methods
         *
         */

        edapInternal.reset = function() {
            edapThis.logs = {
                error: []
            };

            edapInternal.inError = false;
            edapInternal.listeners = {};
            edapInternal.events = {};
            edapInternal.history = [];
            edapInternal.scopes = {};
            edapInternal.savedCmds = null;
            edapInternal.config = {
                maxWaitTime: 2  // in seconds
            };
        };


        /**
         *  Takes an Array instance and returns an object where the keys are the array
         *  index values and the values are true.
         *
         *  @private
         *  @param {Array} [arr] - Array of toString()'able values
         *  @returns {Object} - Keys are the arr values, values are set to true.
         */
        edapInternal.arrayToObject = function(arr) {
            var ret = {},
                i,
                len,
                err;

            if (!(arr instanceof Array)) {
                err = new TypeError('arrayToObject(): arr not passed an Array');
                edapThis.error(err);
                throw err;
            }

            for (i = 0, len = arr.length; i < len; i++) {
                ret[arr[i]] = true;
            }

            return ret;
        };


        /**
         *  History logger.
         *  All log entries will be available in Edap.internal.history
         *
         *  @private
         *  @param {String} [log] - Wraps the String in an Array and pushes into the log
         */
        edapInternal.logHistory = function(log) {
            edapInternal.history.push([log]);
        };


        /**
         *  Should be used only once when flushing any existing window.edap queued commands.
         *  It will run each command by passing it into Edap.push().
         *  @see edapThis.push
         *
         *  @private
         *  @param {Array} [cmds] - Array of Arrays of commands to execute
         */
        edapInternal.flushAsyncCommands = function(cmds) {
            var currentCmd;

            while (cmds.length > 0) {
                currentCmd = cmds.shift(); // remove elements from array as we go to prevent any looping
                edapThis.push.call(edapThis, currentCmd);
            }
        };


        /**
         *  Gets the Persistent Logging instance used by EDAP
         *
         *  @private
         *  @returns {Object} - the Basil persistent logger instance used by EDAP
         */
        edapInternal.getPersistentLogger = function() {
            return persistentLogger;
        };

        /**
         *  Stores a command into persistent storage. Currently the order of preference on client-side
         *  storage is:
         *    - localStorage
         *    - cookie
         *
         *  @private
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Boolean} - true: if stored, false: if not
         */
        edapInternal.storeCmd = function(eventName, data) {
            var existingCmds,
                err;

            if (typeof eventName !== 'string') {
                err = new TypeError('storeCmd(): eventName must be a string');
                edapThis.error(err);
                throw err;
            }

            try {
                existingCmds = persistentLogger.get(loggingKey);

                if (existingCmds === null) {
                    existingCmds = edapPublic.clone(defaultCmdsFormat);
                }
            } catch (e) {
                edapThis.error(e);
                existingCmds = edapPublic.clone(defaultCmdsFormat);
            }

            if (existingCmds && existingCmds.hasOwnProperty('ver')) {
                existingCmds.cmds.push([eventName, data]);
                persistentLogger.set(loggingKey, existingCmds);
                return true;
            }

            return false;
        };


        /**
         *  Removes an item from client-side storage based on matching the eventName and JSON.stringify
         *  matching the data.
         *
         *  @private
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Boolean} - true: if successful, false: if not
         */
        edapInternal.removeCmd = function(eventName, data) {
            var ret = false,
                existingCmds,
                jsonData,
                i,
                len;

            try {
                existingCmds = persistentLogger.get(loggingKey);
                jsonData = JSON.stringify(data);

                if (existingCmds === null) {
                    return true;
                }

                if (existingCmds && existingCmds.hasOwnProperty('ver')) {
                    for (i = 0, len = existingCmds.cmds.length; i < len; i++) {
                        if (eventName === existingCmds.cmds[i][0] && jsonData === JSON.stringify(existingCmds.cmds[i][1])) {
                            existingCmds.cmds.splice(i, 1);

                            // Force this loop to end
                            i = len;
                        }
                    }
                    persistentLogger.set(loggingKey, existingCmds);

                    ret = true;
                } else {
                    edapThis.error(new TypeError('removeCmd found incorrectly formatted cmd data: ' + JSON.stringify(existingCmds)));
                }
            } catch (e) {
                edapThis.error(e);
            }

            return ret;
        };


        /**
         *  Execute all the client-side stored events
         *
         *  @private
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Doneable} - Will resolve when all the stored events have completed processing
         */
        edapInternal.processStoredCmds = function() {
            return new Doneable(function(done) {
                var completeCmds = 0,
                    existingCmds,
                    err,
                    numCmds,
                    i;

                function checkDone() {
                    completeCmds++;
                    if (completeCmds === numCmds) {
                        done();
                    }
                }

                function triggerCmd(idx) {
                    var eventName = existingCmds.cmds[idx][0],
                        data = existingCmds.cmds[idx][1];

                    edapThis.trigger(eventName, data).then(function() {
                        edapInternal.removeCmd(eventName, data);
                        checkDone();
                    });
                }

                try {
                    existingCmds = persistentLogger.get(loggingKey);
                } catch (e) {
                    edapThis.error(e);
                    done();
                    return;
                }

                if (existingCmds === null) {
                    done();
                    return;
                }

                if (!existingCmds || !existingCmds.hasOwnProperty('ver')) {
                    err = new TypeError('processStoredCmds(): stored values not in the correct format: ' + JSON.stringify(existingCmds));
                    edapThis.error(err);
                    done();
                    return;
                }

                numCmds = existingCmds.cmds.length;

                for (i = 0; i < numCmds; i++) {
                    triggerCmd(i);
                }
            }, {error: edapThis.error});
        };


        /**
         *  Gets a list of related listener uuids to an event name. Events can be hierarchical so the the
         *  hierarchy is traversed in most specific to least specific order. Example:
         *    eventName: 'test.foo.bar'
         *    Callbacks would be concatentated in the following order:
         *      test.foo.bar
         *      test.foo.*
         *      test.*
         *      *
         *
         *  @private
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @returns {Array} - Array of listener uuids related to eventName in
         *                     most specific to least specific order
         */
        edapInternal.getCallbacks = function(eventName) {
            var logName = 'getCallbacks',
                ret = [],
                parts,
                newParts,
                newEventName,
                err,
                i;

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapThis.error(err);
                throw err;
            }

            parts = eventName.split('.');
            for (i = parts.length; i > 0; i--) {
                newParts = parts.slice(0, i);
                newEventName = newParts.join('.');

                if (eventName === newEventName) {
                    if (edapInternal.events.hasOwnProperty(newEventName)) {
                        ret = ret.concat(edapInternal.events[newEventName]);
                    }
                }

                newParts[i - 1] = '*';
                newEventName = newParts.join('.');

                if (edapInternal.events.hasOwnProperty(newEventName)) {
                    ret = ret.concat(edapInternal.events[newEventName]);
                }
            }

            return ret;
        };


        /**
         *  Parses the edapConfig cookie and loads the config appropriately
         *  Currently it supports the following options:
         *      logging: 'on' | 'off' - force enable/disable logging regardless of environment
         *      logLevel: see consolehelpers - sets the console logging level where messages are logged
         */
        edapInternal.setupFromCookie = function() {
            // Setup the console logging logic
            configCookieData = cookieParser.getConfigData();

            if (configCookieData.logging === 'on') {
                edapPublic.console.enable();
            } else if (configCookieData.logging === 'off') {
                edapPublic.console.disable();
            } else {
                env = edapThis.getEnv();

                // By default disable it for all environments
                edapPublic.console.disable();

                // Enable it only for certain environments if not overwritten by the cookie
                if (env === 'test' || env === 'stage' || env === 'dev') {
                    edapPublic.console.enable();
                }
            }

            if (configCookieData.hasOwnProperty('logLevel')) {
                edapPublic.console.setLogLevel(configCookieData.logLevel);
            }
        };


        /**
         *  Attempts to create a new unique GUID.
         *
         *  @returns {Object} - rfc4122 version 4 compliant guid, or null if it cannot create a unique guid
         */
        edapInternal.getUniqUuid = function() {
            var maxUuidAttempts = 10,
                uuid = edapPublic.createUuid(),
                i;

            for (i = 0; i < maxUuidAttempts; i++) {
                if (!edapInternal.listeners.hasOwnProperty(uuid)) {
                    return uuid;
                }

                uuid = edapPublic.createUuid();
            }

            edapThis.error(new Error('could not generate a unique UUID'));

            return null;
        };


        /**
         *  Executes a listener with the passed in data
         *
         *  @private
         *  @param {String} [uuid] - rfc4122 version 4 compliant guid, must be registered via on() or once()
         *  @param {Object} [data] - data object to pass into the listener
         *  @returns {Doneable} - Resolves when the listeners callback has been resolved
         */
        edapInternal.executeListener = function(uuid, data) {
            var logName = 'executeListener';

            return new Doneable(function(done) {
                var isAsync = false,
                    uuidRecord,
                    err;

                function setAsync() {
                    isAsync = true;
                }

                try {
                    uuidRecord = edapInternal.listeners[uuid];
                    uuidRecord.func.call(edapThis, data, setAsync, done);
                    uuidRecord.executedCount++;

                    if (!isAsync) {
                        done();
                    }
                } catch (e) {
                    err = new Error(logName + '(): error caught in processing listener: ' + uuid + ' stacktrace:' + e.stack);
                    edapThis.error(err, data);
                    done();
                }
            }, {
                error: edapThis.error
            });
        };


        /**
         *  Builds the data object to be used in trigger(). It will extend optData over the ADL allowing anything
         *  in the optData to overwrite an ADL value.
         *
         *  @private
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - name of the event to be added to the optData object
         *  @param {Object} [optData] - Priority data object that will be extended over the ADL
         *  @param {Boolean} [forceData] - if set to true then optData will be cloned and returned, not extended over the ADL
         *  @returns {Object} - extended data object from the ADL and passed in optData param
         */
        edapInternal.buildTriggerData = function(eventName, optData, forceData) {
            var err;

            if (typeof eventName !== 'string') {
                err = new TypeError('buildTriggerData(): called without an eventName');
                edapThis.error(err);
                throw err;
            }

            if (forceData) {
                return edapPublic.clone(optData);
            }

            optData = optData || {};
            optData.edapeventname = eventName;
            return edapPublic.extend(edapThis.getDL(), optData);
        };


        /**
         *  Creates a listener in the EDAP internals
         *
         *  @private
         *  @param {Function} [cb] - Callback to use when creating the listener
         *  @returns {String} - UUID of the listener or null if no UUID can be generated
         */
        edapInternal.createListener = function(cb) {
            var uuid = edapInternal.getUniqUuid();

            if (uuid !== null) {
                // Store the callback in the listeners lookup table
                edapInternal.listeners[uuid] = {
                    func: cb,
                    executedCount: 0
                };
            }

            return uuid;
        };


        /**
         *  Removes listener from the EDAP internals
         *
         *  @private
         *  @param {String} [uuid] - UUID of the listener to remove
         */
        edapInternal.removeListener = function(uuid) {
            // If it doesn't exist then return as if we deleted it
            if (edapPublic.getListener(uuid) === null) {
                return;
            }

            // if it does exist then delete it
            delete edapInternal.listeners[uuid];
        };


        /**
         *  Removes listener from EDAP and any events that it was registered.
         *
         *  @private
         *  @param {String} [uuid] - UUID of the listener to remove
         */
        edapInternal.removeListenerFromEvents = function(uuid) {
            var logName = 'removeListenerFromEvents',
                eventsIndex,
                uuidIndex,
                eventsIndexLen,
                evtUuidlen,
                events,
                evt;

            // We don't return immediately if it isn't a listener because we want to do our best to make sure
            // everything is cleaned up properly. That way if the listener object is out-of-sync from the events
            // object then at least we will have made sure this uuid is removed from both.

            events = edapPublic.getEventNames();

            // Delete the Listener
            edapInternal.removeListener(uuid);

            // Find which event it was registered for and removed it from the list of listeners
            for (eventsIndex = 0, eventsIndexLen = events.length; eventsIndex < eventsIndexLen; eventsIndex++) {
                evt = edapPublic.getEvent(events[eventsIndex]);
                for (uuidIndex = 0, evtUuidlen = evt.length; uuidIndex < evtUuidlen; uuidIndex++) {
                    if (evt[uuidIndex] === uuid) {
                        // NOTE: Splicing an array while looping over it is dangerous. This only works because we bail out of
                        //       the immediately after splicing the array. If this ever changes then this logic will need to be
                        //       modified to make sure we actually loop over what we think we loop over.
                        evt.splice(uuidIndex, 1);
                        edapInternal.logHistory([logName + '():', 'removed uuid', uuid, 'from', events[eventsIndex], 'queue'].join(' '));
                        edapThis.trigger(edapListenerRemovedName, {edapremovedeventname: evt, edaplisteneruuid: uuid});
                        return;
                    }
                }
            }
        };


        /**
         *  Adds listeners UUIDs to an event so that when the event is trigger()'d the listener will be called
         *
         *  @private
         *  @param {String} [eventName] - name of event
         *  @param {String} [listenerUuid] - UUID of the listener to add
         */
        edapInternal.addListenerToEvent = function(eventName, listenerUuid) {
            var events = edapInternal.events,
                evt = edapPublic.getEvent(eventName);

            // Store the UUID of the callback with the associated event
            if (evt instanceof Array) {
                evt.push(listenerUuid);
            } else {
                events[eventName] = [listenerUuid];
            }
        };


        /**
         *  EDAP underscore/lodash instance
         *
         *  @private
         */
        edapInternal._ = _;


        /**
         *
         * Public Methods
         *
         */


        /**
         *  Array of internal events that EDAP fires
         *
         *  @public
         */
        edapPublic.coreSupportedEvents = coreSupportedEvents;


        /**
         *  Array of internal events that EDAP fires
         *
         *  @public
         */
        edapPublic.ignoredConsoleEvents = ignoredConsoleEvents;


        /**
         *  Helper function that will return if an eventName is used by the core EDAP system
         *
         *  @public
         *  @param {String} [eventName] - Name of the event
         *  @returns {Boolean} - if the eventName is in the coreSupportedEvents list
         */
        edapPublic.isCoreEdapEvent = function(eventName) {
            return _.indexOf(edapPublic.coreSupportedEvents, eventName) > -1;
        };


        /**
         *  Helper function that will return if an eventName is supposed to be ignored
         *  when logging events to console
         *
         *  @public
         *  @param {String} [eventName] - Name of the event
         *  @returns {Boolean} - if the eventName is in the ignoredConsoleEvents list
         */
        edapPublic.isIgnoredConsoleEvent = function(eventName) {
            return _.indexOf(edapPublic.ignoredConsoleEvents, eventName) > -1;
        };


        /**
         *  Recursive clone to give you a deep copy of your Object.
         *
         *  @link https://lodash.com/docs#clone
         *  @public
         *  @param {*} [src] - Object to clone
         *  @returns {Object} - New deeply cloned object from the src.
         */
        edapPublic.clone = function(src) {
            return _.clone(src, true);
        };


        /**
         *  Will return a cloned and extended object that is assigned from left to right, last one wins
         *
         *  @link https://lodash.com/docs#assign
         *
         *  @example
         *  edap.public.extend({ 'user': 'barney' }, { 'age': 40 }, { 'user': 'fred' });
         *  returns: { 'user': 'fred', 'age': 40 }
         *
         *  @public
         *  @param {...Object} - Source objects that will be assigned from left to right, last one wins
         *  @returns {Object} - The destination object
         */
        edapPublic.extend = function() {
            var args = Array.prototype.slice.call(arguments);
            args.unshift({});
            return edapPublic.clone(_.assign.apply(_, args));
        };


        /**
         *  Create a UUID
         *  Borrowed from core-ui-utilities repo
         *  @link http://byronsalau.com/blog/how-to-create-a-guid-uuid-in-javascript/
         *
         *  @public
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        edapPublic.createUuid = function() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                var r = Math.random() * 16 | 0, // eslint-disable-line no-bitwise
                    v = c === 'x' ? r : (r & 0x3 | 0x8); // eslint-disable-line no-bitwise
                return v.toString(16);
            });
        };


        /**
         *  Abstraction around getting the window-level EDAP singleton
         *
         *  @public
         *  @returns {Object} - Returns the window EDAP singleton
         */
        edapPublic.getGlobal = function() {
            return edapHelpers.getEdapGlobal();
        };


        /**
         *  Abstraction around getting the EDAP global options
         *
         *  @public
         *  @returns {Object} - Returns the window EDAP singleton
         */
        edapPublic.getGlobalOptions = function() {
            return edapHelpers.getGlobalOptions();
        };


        /**
         *  ConsoleHelpers instance for logging to the console
         */
        edapPublic.console = new ConsoleHelpers();
        edapPublic.ConsoleHelpers = ConsoleHelpers;


        /**
         *  Gets a Listener object from the EDAP internals
         *
         *  @public
         *  @param {String} [uuid] - UUID of the listener
         *  @param {Object} - Listener object, or null if there is no listener with that UUID
         */
        edapPublic.getListener = function(uuid) {
            if (edapInternal.listeners.hasOwnProperty(uuid)) {
                return edapInternal.listeners[uuid];
            }

            return null;
        };


        /**
         *  Gets an Event object from the EDAP internals
         *
         *  @public
         *  @param {String} [eventName] - name of the Event
         *  @param {Array} - Event Array, or null if there is no event with that name
         */
        edapPublic.getEvent = function(eventName) {
            if (edapInternal.events.hasOwnProperty(eventName)) {
                return edapInternal.events[eventName];
            }

            return null;
        };


        /**
         *  Gets the list of Event names that have listeners registered
         *
         *  @public
         */
        edapPublic.getEventNames = function() {
            return _.keys(edapInternal.events);
        };


        /**
         *  Gets the list of listener objects for a single Event
         *
         *  @public
         *  @param {String} [eventName] - Event to get the list of listeners
         *  @returns {Array} - List of Listener objects associated with an Event
         *                     or null if the Event does not exist
         */
        edapPublic.getListenersForEvent = function(eventName) {
            var retData = [],
                eventRef,
                i,
                len;

            eventRef = edapPublic.getEvent(eventName);

            if (eventRef === null) {
                return null;
            }

            for (i = 0, len = eventRef.length; i < len; i++) {
                retData.push(edapPublic.getListener(eventRef[i]));
            }

            return retData;
        };


        /**
         *  Getter for the window object, mainly for spy-based testing and abstraction
         *
         *  @public
         *  @returns {Object} - The window object
         */
        edapPublic.getWindow = function() {
            return edapHelpers.getWindow();
        };


        /**
         *  Playback instance that manages logging and replaying events
         *
         *  @public
         */
        edapPublic.playback = new Playback(edapThis);


        /**
         *  FeatureFlag constructor for creating new FeatureFlag objects
         *
         *  @public
         */
        edapPublic.FeatureFlag = FeatureFlag;


        /**
         *  Features object that provides access to any FeatureFlags loaded in EDAP
         *
         *  @public
         */
        edapPublic.features = new Features(edapThis);


        /**
         *
         *  Core Methods
         *
         */


        /**
         *  Assign a callback to an event.
         *  Callbacks can be either synchronous or asynchronous. See examples.
         *
         *  NOTE: If you create an async callback you MUST be sure that the callback calls
         *        the async() function in normal synchronous execution of the function, and
         *        guarentee that done() is always called at some point. Not calling done()
         *        will block the Edap.trigger Doneable from resolving and running any associated
         *        callbacks.
         *
         *  @example
         *  // Non-async callback function assigned to the 'myEvent' event
         *  var uuid = Edap.on('myEvent', function(data) {
         *      // do something when 'myEvent' is fired. You also have access to all the data!
         *  });
         *
         *  @example
         *  // Async callback function assigned to the 'myEvent' event
         *  // NOTE: It you are creating a sync callback you MUST
         *  var uuid = Edap.on('myEvent', function(data, async, done) {
         *      async();
         *      // Notice I assigned to .always() instead of .done() to guarentee done()
         *      // is called even if the GET request fails.
         *      $.get('/some/endpoint').always(done);
         *  });
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event, cannot be a uuid
         *  @param {Function} [cb] - The callback to register with the event. See examples for more details
         *  @param {Object} [opts] - Supported options:
         *                            - logName: overrides the existing logName to put into Edap history
         *  @returns {String|null} - rfc4122 version 4 compliant guid or null if a guid could not be created
         */
        edapThis.on = function(eventName, cb, opts) {
            var uuid,
                logName,
                err;

            opts = opts || {};
            logName = opts.logName || 'on';

            edapInternal.logHistory(['Called', logName + '()', 'for event', eventName, 'with', JSON.stringify(opts)].join(' '));

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapThis.error(err);
                throw err;
            }

            if (eventName.match(uuidRegex)) {
                err = new TypeError(logName + '(): eventName cannot be a uuid');
                edapThis.error(err);
                throw err;
            }

            if (typeof cb !== 'function') {
                err = new TypeError(logName + '(): cb must be a function');
                edapThis.error(err);
                throw err;
            }

            // Get the unique UUID
            uuid = edapInternal.createListener(cb);

            if (uuid === null) {
                err = new TypeError(logName + '(): cannot create UUID');
                edapThis.error(err);
                return null;
            }

            edapInternal.addListenerToEvent(eventName, uuid);

            edapInternal.logHistory([logName + '():', 'Added event listener for', eventName].join(' '));
            edapThis.trigger(edapListenerAddedName, {edapaddedeventname: eventName, edaplisteneruuid: uuid});

            return uuid;
        };


        /**
         *  Removes a callback. See examples.
         *
         *  @example
         *  // Remove all the callbacks for a specific event
         *  Edap.off('myEvent');
         *
         *  @example
         *  Removes the uuid from being a callback
         *  Edap.off(uuid);
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventNameOrUuid] - event name or uuid to remove
         */
        edapThis.off = function(eventNameOrUuid) {
            var logName = 'off',
                uuids,
                i,
                len,
                err;

            if (typeof eventNameOrUuid === 'string') {
                // Passed in a UUID
                if (eventNameOrUuid.match(uuidRegex)) {
                    edapInternal.logHistory(['Called', logName + '()', 'with uuid', eventNameOrUuid].join(' '));

                    edapInternal.removeListenerFromEvents(eventNameOrUuid);

                // Passed in an event name
                } else {
                    edapInternal.logHistory(['Called', logName + '()', 'with string', eventNameOrUuid].join(' '));

                    uuids = edapPublic.getEvent(eventNameOrUuid);

                    if (uuids !== null) {
                        // Raw access to the edapInternals.events so we can set the [] directly
                        edapInternal.events[eventNameOrUuid] = [];

                        // Make sure we trigger a "removedListener" event and remove the callback from the listeners hash
                        for (i = 0, len = uuids.length; i < len; i++) {
                            // while it is possible that something may be out-of-sync, we should always publish a "removed" message in an attempt to
                            // get everything else in sync. The intent is to delete a listener, so even if it doesn't currently exist it is still
                            // deleted after finishing this removeListener() call
                            edapInternal.removeListener(uuids[i]);
                            edapThis.trigger(edapListenerRemovedName, {edapremovedeventname: eventNameOrUuid, edaplisteneruuid: uuids[i]});
                        }

                        edapInternal.logHistory([logName + '():', 'cleared all cbs for', eventNameOrUuid, 'event'].join(' '));
                    }
                }
            } else {
                edapInternal.logHistory(['Called', logName + '()', 'invalid'].join(' '));

                err = new TypeError(logName + '(): must be passed an event name string or a function');
                edapThis.error(err);
                throw err;
            }

            return;
        };


        /**
         *  Sets a callback for an event that will only fire once. Supports all types of callbacks
         *  supported by on();
         *  @see this.on
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @param {Function} [cb] - The callback to register with the event. See on() examples for more details
         *  @param {Object} [opts] - Supported options: see on() options
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        edapThis.once = function(eventName, cb, opts) {
            var onUuid;

            // Remove the listener before executing the callback so that it is impossible to get in an infinite loop
            // if the callback triggers the same eventName again.
            function runOnce() {
                edapThis.off(onUuid);
                cb.apply(edapThis, arguments);
            }

            opts = opts || {};
            opts.logName = 'once';

            onUuid = edapThis.on(eventName, runOnce, opts);

            return onUuid;
        };


        /**
         *  Trigger an event and fire all the associated callbacks
         *
         *  @example
         *  // Firing an event with only the Data Layer being passed
         *  Edap.trigger('myEvent');
         *
         *  @example
         *  // Firing an event with event-specific data only the Data Layer being passed
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2});
         *
         *  @example
         *  // Firing an event that should be queued for processing later
         *  // This mechanism should be used when firing an event near a page transition
         *  // so the next page can process the event, increasing the odds that the event
         *  // is processed fully.
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}, {delayProcessing: true});
         *
         *  @example
         *  // Sets a maxWaitTime of 4 seconds to finish processing all event listeners
         *  // related to the 'myEvent' event. Overwrites the default maxWaitTime in
         *  // edap.internal.config.maxWaitTime.
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}, {maxWaitTime: 4});
         *
         *  @example
         *  // Firing an event and assigning a callback when all the event processing
         *  // has completed
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}).then(function() {
         *      // do something now that all the event callbacks are done
         *  });
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event, cannot trigger wildcard event explicitly
         *  @param {Function} [cb] - The callback to register with the event. See examples for more details
         *  @param {Object} [opts] - Supported options:
         *                            - delayProcessing: queue this event and data up to be processed on the next 'edap.flush' event
         *                            - maxWaitTime: time in seconds to wait for callbacks to complete before calling the
         *                                           trigger complete callback. Overwrites the Edap default maxWaitTime of 2 seconds.
         *                            - forceData: when set to true the data passed into trigger is the data that will be passed to
         *                                         each listener. The data will not be extended over the ADL.
         *                            - playbackObj: The Playback object to use when logging listener execution. If this is set then
         *                                           trigger() will exclude any listener UUIDs that have already been called and add
         *                                           any new listener UUIDs to the list based, if there are any that have not been executed.
         *                                           This option is used when replaying events. The code assumes that by passing this object
         *                                           that you are attempting to replay the event. It is not a common option to be passed.
         *  @returns {Doneable} - Resolves when all the callbacks for the event are done
         */
        edapThis.trigger = function(eventName, data, opts) {
            var logName = 'trigger',
                waitTime = edapInternal.config.maxWaitTime,
                validOpts = ['delayProcessing', 'maxWaitTime', 'forceData', 'playbackObj'],
                shouldConsoleLog = true,
                err;

            // Set opts to a default empy object if missing
            if (_.isObject(opts)) {
                // Validate that only allowed options are passed in
                _.forOwn(opts, function(value, key) {
                    if (_.indexOf(validOpts, key) === -1) {
                        err = new TypeError(logName + '(): called with invalid option: ' + key);
                    }
                });
            } else {
                opts = {};
            }

            edapInternal.logHistory(['Called', logName + '()', 'for event', eventName, 'with data', JSON.stringify(data), 'with opts', JSON.stringify(opts)].join(' '));

            // If there was an options validation error then throw it now that we have logged the history event
            if (err) {
                edapThis.error(err);
                throw err;
            }

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapThis.error(err);
                throw err;
            }

            if (typeof data !== 'object' && data !== null && typeof data !== 'undefined') {
                err = new TypeError(logName + '(): data must be an Object if defined');
                edapThis.error(err);
                throw err;
            }

            if (eventName.match(/[*]$/)) {
                err = new TypeError(logName + '(): cannot trigger wildcard event explicitly ' + eventName);
                edapThis.error(err);
                throw err;
            }

            // Don't log if we find this eventName in our internal event list
            if (edapPublic.isIgnoredConsoleEvent(eventName)) {
                shouldConsoleLog = false;
            }

            return new Doneable(function(done) {
                var listenerUuids = edapInternal.getCallbacks(eventName),
                    completeListeners = 0,
                    previouslyExecutedUUIDs = {},
                    playbackObj = null,
                    maxWaitTimeout,
                    numListeners,
                    clonedData,
                    i;

                function allListenersComplete() {
                    clearTimeout(maxWaitTimeout);

                    if (listenerUuids.length) {
                        edapInternal.logHistory(['Executed', listenerUuids.length, 'listeners total for event', eventName].join(' '));
                    }

                    done();
                }

                function listenerComplete() {
                    completeListeners++;
                    if (completeListeners === numListeners) {
                        allListenersComplete();
                    }
                }

                function logIfWeShould(func) {
                    if (shouldConsoleLog && typeof func === 'function') {
                        func();
                    }
                }

                function ifPlaybackObjNull(func) {
                    if (playbackObj === null && typeof func === 'function') {
                        func();
                    }
                }

                function defaultToObj(obj) {
                    if (obj === undefined || obj === null) {
                        return {};
                    }
                    return obj;
                }

                logIfWeShould(function() {
                    edapPublic.console.startLog((opts.playbackObj ? '(replay) ' : '') + 'EDAP event triggered: ' + eventName);
                    edapPublic.console.log('Passed Data: ', data);
                    edapPublic.console.log('Options: ', opts);
                });

                data = defaultToObj(data);

                if (opts.delayProcessing === true) {
                    // Get our cloned data set
                    clonedData = edapInternal.buildTriggerData(eventName, data, opts.forceData);

                    logIfWeShould(function() {
                        edapPublic.console.endLog('Shrinkwrapped Data: ', clonedData);
                    });

                    edapInternal.storeCmd(eventName, clonedData);

                    edapInternal.logHistory(['Delayed processing for event', eventName].join(' '));

                    // Hardcode this to blank so that we don't log a history line
                    listenerUuids = [];

                    allListenersComplete();
                    return;
                }

                // If we are passed a playbackObj then let's assign it and extract the data needed
                if (opts.playbackObj) {
                    playbackObj = opts.playbackObj;
                    previouslyExecutedUUIDs = edapInternal.arrayToObject(playbackObj.executedListeners);
                }

                if (typeof opts.maxWaitTime === 'number') {
                    // Overwrite the default on a per-trigger basis
                    waitTime = opts.maxWaitTime;
                }

                // waitTime must be > 0 to assign the Timeout
                if (waitTime > 0) {
                    maxWaitTimeout = setTimeout(function() {
                        edapInternal.logHistory([logName + '()', waitTime + 'sec', 'timeout reached for event', eventName].join(' '));
                        allListenersComplete();
                    }, waitTime * 1000);
                }

                // If there are no callbacks then just return a done Doneable
                if (listenerUuids.length === 0) {
                    // Even though there are no listeners we still want to log the event in the playback log in case there are
                    // listeners if/when it is replay()'d later
                    ifPlaybackObjNull(function() {
                        // Get our cloned data set. This is duplicated in a few spots so that we are only doing this expensive clone() operation
                        // when we absolutely have to. The code is a bit more complex, but for the sake of performance.
                        clonedData = edapInternal.buildTriggerData(eventName, data, opts.forceData);

                        playbackObj = edapPublic.playback.addEvent(eventName, clonedData);
                    });

                    logIfWeShould(function() {
                        edapPublic.console.endLog('Shrinkwrapped Data: skipped (no callbacks registered)');
                    });

                    allListenersComplete();
                    return;
                }

                // Get our cloned data set. This is duplicated in a few spots so that we are only doing this expensive clone() operation
                // when we absolutely have to. The code is a bit more complex, but for the sake of performance.
                clonedData = edapInternal.buildTriggerData(eventName, data, opts.forceData);

                // If we don't have a Playback object then get one now.
                ifPlaybackObjNull(function() {
                    playbackObj = edapPublic.playback.addEvent(eventName, clonedData);
                });

                logIfWeShould(function() {
                    edapPublic.console.endLog('Shrinkwrapped Data: ', clonedData);
                });

                // Now let's loop through the listeners and execute any that have not already been executed.
                for (i = 0, numListeners = listenerUuids.length; i < numListeners; i++) {
                    // Don't execute a listener that has already been executed. This only applies to playback scenarios.
                    if (!previouslyExecutedUUIDs.hasOwnProperty(listenerUuids[i])) {
                        // playbackObj === null would mean that playback was disabled
                        if (playbackObj !== null) {
                            playbackObj.executedListeners.push(listenerUuids[i]);
                        }

                        edapInternal.executeListener(listenerUuids[i], clonedData).then(listenerComplete);

                    // if it has already been executed then just log it as complete immediately
                    } else {
                        listenerComplete();
                    }
                }
            }, {
                error: function() {
                    if (shouldConsoleLog) {
                        edapPublic.console.endLog('Error Caught');
                    }
                    edapThis.error(err);
                }
            });
        };


        /**
         *  Error logger.
         *  All log entries will be available in Edap.logs.error
         *
         *  @method
         *  @param {Error|String} [err] - If a String is passed then it will be used to instantiate a new Error.
         *                                If an instance of Error is passed then it will be pushed as is.
         *  @param {Object} [optAdl] - Optional ADL to associate with the edap.error event that is triggered instead
         *                             of the normal way of passing the pages ADL. This is very useful for maintaining
         *                             the state of the error for trigger-based errors and aides in debugging. "logmessage"
         *                             will be added regardless if you use this field or not.
         */
        edapThis.error = function(err, optAdl) {
            var savedLogLevel = edapPublic.console.getLogLevel(),
                errorLogMessageField = 'errorlogmessage',
                triggerOpts = null,
                eventData,
                newErr,
                errStr;

            if (!err) {
                return;
            }

            try {
                if (err instanceof Error) {
                    newErr = err;
                } else {
                    newErr = new Error(err);
                }

                // Now build the errStr to send in edap.error event
                if (typeof newErr.stack === 'string') {
                    errStr = newErr.stack;
                } else {
                    errStr = newErr.toString();
                }
            } catch (e) {
                newErr = err.toString();
                errStr = newErr;
            }

            // Log the error as a real console.error log (when applicable)
            edapPublic.console.setLogLevel('error');
            edapPublic.console.log('EDAP error: ' + errStr);
            edapPublic.console.setLogLevel(savedLogLevel);

            // Save the error to our internal log
            edapThis.logs.error.push(newErr);

            // Let's make sure we don't cause an infinite error loop here
            if (edapInternal.inError === false) {
                edapInternal.inError = true;

                eventData = {};

                // If we have an optional ADL then we need to use that instead of create our own
                // when we trigger the edap.error event
                if (optAdl) {
                    eventData = edapPublic.clone(optAdl);

                    // We need to keep our messages backwards compatible so keep the "edapeventname"
                    // property matching the actual event, but in this case we will add "erroredapeventname"
                    // that was the original event name
                    eventData.erroredapeventname = eventData.edapeventname;
                    eventData.edapeventname = edapError;

                    triggerOpts = {
                        forceData: true
                    };
                }

                // Add the error message to the eventData
                eventData[errorLogMessageField] = errStr;

                edapThis.trigger(edapError, eventData, triggerOpts).then(function() {
                    edapInternal.inError = false;
                });
            }
        };


        /**
         *  Used to mimic the async version of Edap.
         *
         *  @example
         *  // Snippet that shows how to use the push mechanism
         *  window.edap = window.edap || [];
         *  window.edap.push(function(edap) {
         *      edap.on('that.cool.event', function(data) {
         *          //do something cool!
         *      });
         *  });
         *
         *  @method
         *  @param {Function} [fn] - A function that will be passed the edap instance as the
         *                             function parameter
         */
        edapThis.push = function(fn) {
            var logName = 'push',
                err;

            edapInternal.logHistory(['Called', logName + '()'].join(' '));

            if (typeof fn !== 'function') {
                err = new TypeError(logName + '(): fn must be a Function');
                edapThis.error(err);
                throw err;
            }

            try {
                fn.call(edapThis, edapThis);
            } catch (e) {
                err = new Error(logName + '(): error caught in executing function: ' + JSON.stringify(fn) + ' stacktrace:' + e.stack);
                edapThis.error(err);
            }
        };


        /**
         *  Get a info on a single scope or all scopes
         *
         *  @method
         *  @param {String} [optScopeName] - optional scope name to get
         *  @returns {Object} - if optScopeName is passed then it will return the scope info for that scope or
         *                      null if that scope name does not exist,
         *                      otherwise it will return object of all scopes
         */
        edapThis.getScopeInfo = function(optScopeName) {
            var logName = 'getScopeList';

            edapInternal.logHistory(['Called', logName + '()'].join(' '));

            if (typeof optScopeName === 'string') {
                return edapInternal.scopes[optScopeName] || null;
            }

            return edapInternal.scopes;
        };


        /**
         *  Get the scope-specific data for a scope
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [scopeName] - optional scope name to get
         *  @returns {Object} - Data object for a scope or null
         */
        edapThis.getScopeData = function(scopeName) {
            var logName = 'getScopeData',
                scopeInfo,
                err;

            if (typeof scopeName !== 'string') {
                err = new TypeError(logName + '(): scopeName was not a string');
                edapThis.error(err);
                throw err;
            }

            scopeInfo = edapThis.getScopeInfo(scopeName);

            if (scopeInfo !== null) {
                return scopeInfo.data;
            }

            return null;
        };


        /**
         *  Creates a scope (or context) within Edap that provides 2 things:
         *      1. Guaranteed unique context names. This can be used to prevent modules from registering
         *         their event listeners multiple times.
         *      2. Provides Scope.prototype.unregister() that will unregister any listeners that were
         *         set through the Scope. The purpose would be to limit the amount of application logic
         *         needed to cleanup any module-specific listeners when the module is destoyed.
         *      3. Provides Scope.prototype.destroy() to remove a scope
         *      4. Provides Scope.prototype.getData() as a scope-specific data store
         *
         *  This proxies all the Edap methods and features so it has access to all the same methods.
         *
         *  @example
         *  // simple example of all the exposed methods of a Scope
         *  var myScope = new Edap.Scope('myScopeName');
         *  var onUuid = myScope.on('test', function(data){...});
         *  myScope.once('test', function(data){...});
         *  myScope.trigger('myEvent',{testData: true});
         *  myScope.off(onUuid);
         *  myScope.log('myScopeName just logged a msg');
         *  myScope.error('myScopeName just logged a error');
         *  myScope.getScopeData().myDataEl = 1;
         *  myScope.unregister();
         *  myScope.destroy();
         *
         *  @constructor
         *  @throws {TypeError} - when not called with "new" or invalid name argument
         *  @returns {Edap.Scope} - this Edap.Scope instance
         */
        edapThis.Scope = function(name) {
            var scope = this,
                removeListener,
                err;

            if (scope === edapThis) {
                err = new TypeError('Edap.Scope must be called with the "new" keyword');
                edapThis.error(err);
                throw err;
            }

            if (typeof name !== 'string' || name.length === 0) {
                err = new TypeError('name must be a non-zero length string');
                edapThis.error(err);
                throw err;
            }

            if (edapInternal.scopes.hasOwnProperty(name) && edapInternal.scopes[name] !== undefined) {
                err = new TypeError(name + ' scope already registered');
                edapThis.error(err);
                throw err;
            }

            edapInternal.scopes[name] = {
                instance: scope,
                data: {}
            };

            // This loops over all the edap top-level properties and sets them to this scope's properties (proxies then through the scope)
            edapInternal._.forOwn(edapThis, function(value, propName) {
                scope[propName] = edapThis[propName];
            });

            // This is overwritting the proxied edap.on method so that we can hook into when a new listener is created through this
            // scope and keep track of it accordingly
            scope.on = function(eventName) {
                var uuid = edapThis.on.apply(edapThis, arguments);

                if (uuid !== null) {
                    scope.uuids[uuid] = true;
                    edapInternal.logHistory(['Scope', scope.name, 'registered new callback for event', eventName, 'uuid', uuid].join(' '));
                }

                return uuid;
            };

            /**
             *  Calls edap.off() for all event listeners registered through this scope (scope.on())
             *
             *  @method
             */
            scope.unregister = function() {
                edapInternal.logHistory(['Called unregister() for Scope', scope.name].join(' '));

                edapInternal._.forOwn(scope.uuids, function(value, uuid) {
                    edapThis.off(uuid);
                    delete scope.uuids[uuid];
                });
            };

            /**
             *  Destroys this scope instance. It does so in the following way:
             *    1. Unregisters all event listeners on()'d by this scope
             *    2. Deletes all non-function properties of this scope
             *    3. Sets all function properties to this scope to an exception throwing function
             *    4. Sets the scope to undefined in the EDAP internal scopes list
             *
             *  @method
             */
            scope.destroy = function() {
                var oldName = scope.name,
                    deadFunc = function() {
                        throw new Error('Attempting to access destroyed edap scope ' + oldName);
                    };

                edapInternal.logHistory(['Called destroy() for Scope', scope.name].join(' '));

                scope.unregister();
                edapThis.off(removeListener);

                // Replace scope methods with the dead function and delete other properties
                edapInternal._.forOwn(scope, function(value, prop) {
                    if (typeof scope[prop] === 'function') {
                        scope[prop] = deadFunc;
                    } else {
                        delete scope[prop];
                    }
                });

                edapInternal.scopes[oldName] = undefined;
            };


            /**
             *  Gets the data object for this scope.
             *  The same as calling:
             *    edap.getScopeData(scope.name)
             *
             *  @method
             */
            scope.getData = function() {
                return edapThis.getScopeData(scope.name);
            };

            scope.name = name;
            scope.uuids = {};

            // Listen for removed uuids and remove then from our internal uuids
            removeListener = edapThis.on(edapListenerRemovedName, function(data) {
                if (scope.uuids[data.edaplisteneruuid]) {
                    delete scope.uuids[data.edaplisteneruuid];
                    edapInternal.logHistory(['Scope', scope.name, 'removed callback uuid', data.edaplisteneruuid].join(' '));
                }
            });

            return scope;
        };


        /**
         *  Abstraction around getting the global Data Layer
         *
         *  @returns {Object} - Returns the ADL.
         */
        edapThis.getDL = function() {
            var win = edapPublic.getWindow(),
                dl = edapPublic.getGlobalOptions().dataLayerName;

            if (!edapInternal._.has(win, dl)) {
                edapInternal._.set(win, dl, {});
            }

            return edapInternal._.get(win, dl);
        };


        /**
         *  Getter for the current environment (production/stage/test)
         *
         *  @param {Object} [optAdl] - Optional ADL object to use instead of the page ADL
         *  @returns {String} - (optAdl or DL).appenvironment value. Defaults to 'unknown'
         */
        edapThis.getEnv = function(optAdl) {
            var dl;

            if (_.isObject(optAdl)) {
                dl = optAdl;
            } else {
                dl = edapThis.getDL();
            }

            return dl.appenvironment || 'unknown';
        };


        // Constructor code

        // Create our 'init' mark in our eventLog
        edapPublic.playback.setMark('init');

        // Cache the global options for legacy reasons
        edapThis.globalOptions = edapPublic.getGlobalOptions();

        // Disable Playback at startup if set globally
        if (edapThis.globalOptions.skipPlayback === true) {
            edapPublic.playback.disable();
        }

        // Make sure the initial data structure is setup
        edapInternal.reset();

        edapThis.on(edapReset, function() {
            edapInternal.reset();
        });

        // Initialize for the cookie to get logging and other configs setup
        edapInternal.setupFromCookie();

        // Sets up a listener in case someone sets a cookie and doesn't want to reload the page
        edapThis.on(edapLoadCookie, function() {
            edapInternal.setupFromCookie();
        });

        // Listen to the flush event to flush the persistently stored cmds
        edapThis.on(edapFlush, function(data, async, done) {
            async();

            // Every flush we will also re-parse and load the logging config from the cookie
            edapInternal.setupFromCookie();

            if (edapInternal.savedCmds instanceof Array) {
                edapInternal.flushAsyncCommands(edapInternal.savedCmds);
            }

            edapInternal.savedCmds = null; // Clean up this memory
            edapInternal.processStoredCmds().then(done);
        });

        // Save any aysnc-queued commands before we squash it
        edapInternal.savedCmds = edapPublic.getGlobal();

        return edapThis;
    }

    return new Edap();
});

